import type { UploaderConfigEnum } from '@frameio/graphql-client/generated';

export type UploaderConfig = Record<
  UploaderConfigEnum,
  {
    title: string;
    description: string;
    defaultValue: number;
    value?: number;
    min?: number;
    max?: number;
  }
>;

/**
 * For use to define default configurations.
 * As well as the ability to modify using the dev sheet.
 *
 * Can be directly imported into the worker or the main app
 */
export const UploaderConfigSettings: UploaderConfig = {
  PRIORITY_QUEUE_SIZE: {
    title: 'Priority Queue Size',
    description: 'Prority queue size used for reading files from disk',
    defaultValue: 1,
    value: 1,
    min: 0,
    max: 9999999,
  },
  S3_QUEUE_CONCURRENCY_SIZE: {
    title: 'S3 Queue Concurrency Size',
    description:
      'Concurrent number of connections used for uploading files to S3',
    defaultValue: 6,
    value: 6,
    min: 0,
    max: 9999999,
  },
  MAX_UPLOAD_FOLDER_DEPTH: {
    title: 'Max upload folder depth',
    description:
      'Combined folder depth allowed factoring in current depth in project and folder depth being uploaded',
    defaultValue: 10,
    value: 10,
    min: 0,
    max: 20,
  },
  MAX_CONCURRENT_BATCH_LIMIT: {
    title: 'Max Concurrent Batch Limit',
    description: 'Max concurrent batches that can be uploaded at a time',
    defaultValue: 10,
    value: 10,
    min: 0,
    max: 100,
  },
  MAX_FILE_COUNT_PER_JOB: {
    title: 'Max File Count Per Job',
    description: 'Max number of files that can be uploaded in a single job',
    defaultValue: 500,
    value: 500,
    min: 0,
    max: 9999999,
  },
  MAX_CONCURRENT_FILE_LIMIT: {
    title: 'Max Concurrent File Limit',
    description:
      'The total number of files allowed to be uploaded at a time across all concurrent batches',
    defaultValue: 500,
    value: 500,
    min: 0,
    max: 9999999,
  },
  MAX_FOLDER_COUNT_PER_JOB: {
    description: 'Max number of folders that can be uploaded in a single job',
    title: 'Max Folder Count Per Job',
    defaultValue: 250,
    value: 250,
    min: 0,
    max: 9999999,
  },
  UPLOAD_JOB_BATCH_SIZE: {
    description: 'Number of tasks to create per batch api call',
    title: 'Upload Job Batch Size',
    defaultValue: 50,
    value: 50,
    min: 0,
    max: 9999999,
  },
  UPLOAD_JOB_TIMIINGS_UPLOAD_SPEED_MEASURE_SECONDS: {
    description: 'Amount of time used to measure upload speed',
    title: 'Upload Speed Measure Seconds',
    defaultValue: 5,
    value: 5,
    min: 0,
    max: 9999999,
  },
  UPLOAD_JOB_TIMIINGS_REPORT_INTERVAL_MILLISECONDS: {
    description:
      'Amount of time for throttling of reporting status change events',
    title: 'Upload Report Interval Milliseconds',
    defaultValue: 100,
    value: 100,
    min: 0,
    max: 9999999,
  },
  UPLOAD_JOB_TIMIINGS_MARGIN_OF_ERROR_SECONDS: {
    description: 'Sets the margin of error used to measure remaining time',
    title: 'Upload Margin of Error Seconds',
    defaultValue: 0,
    value: 0,
    min: 0,
    max: 9999999,
  },
  /**
   * @todo (ASSETS-1826): allow for checksum verification.
   * We only read when this is enabled.
   */
  VERIFY_CHECKSUM: {
    title: 'Verify Checksum',
    description:
      'Enable the generation of the checksum xxh64 hash with (1). Currently we do not compare this hash to the Backend Response, disable the check with (0)',
    defaultValue: 0,
    value: 0,
    min: 0,
    max: 1,
  },
};
